// Firebase Configuration and Initialization
// This file sets up Firebase services for the MPLC-CRM application

import { initializeApp, FirebaseApp, getApps } from "firebase/app";
import { getAnalytics, Analytics, isSupported } from "firebase/analytics";
import { getAuth, Auth } from "firebase/auth";
import { getFirestore, Firestore } from "firebase/firestore";
import { getStorage, FirebaseStorage } from "firebase/storage";

// Firebase configuration interface
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

// Get Firebase configuration from environment variables
// Using import.meta.env for Vite environment variables
const getFirebaseConfig = (): FirebaseConfig => {
  const config: FirebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
    appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  };

  // Validate required configuration
  const requiredFields = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
  ] as const;

  const missingFields = requiredFields.filter(field => !config[field]);

  if (missingFields.length > 0) {
    console.warn(
      `Firebase configuration incomplete. Missing: ${missingFields.join(', ')}. ` +
      `Firebase features will not be available.`
    );
  }

  return config;
};

// Initialize Firebase app (singleton pattern)
let firebaseApp: FirebaseApp | null = null;
let analytics: Analytics | null = null;
let auth: Auth | null = null;
let firestore: Firestore | null = null;
let storage: FirebaseStorage | null = null;

/**
 * Initialize Firebase application
 * This function ensures Firebase is only initialized once
 */
export const initializeFirebase = (): FirebaseApp | null => {
  try {
    // Check if Firebase is already initialized
    if (getApps().length > 0) {
      firebaseApp = getApps()[0];
      console.log('Firebase already initialized');
      return firebaseApp;
    }

    const config = getFirebaseConfig();

    // Only initialize if we have a valid API key
    if (!config.apiKey) {
      console.warn('Firebase API key not provided. Skipping Firebase initialization.');
      return null;
    }

    // Initialize Firebase
    firebaseApp = initializeApp(config);
    console.log('Firebase initialized successfully');

    return firebaseApp;
  } catch (error) {
    console.error('Error initializing Firebase:', error);
    return null;
  }
};

/**
 * Get Firebase Analytics instance
 * Analytics is only available in browser environments that support it
 */
export const getFirebaseAnalytics = async (): Promise<Analytics | null> => {
  try {
    if (!firebaseApp) {
      initializeFirebase();
    }

    if (!firebaseApp) {
      return null;
    }

    // Check if analytics is already initialized
    if (analytics) {
      return analytics;
    }

    // Check if analytics is supported in this environment
    const analyticsSupported = await isSupported();

    if (!analyticsSupported) {
      console.log('Firebase Analytics not supported in this environment');
      return null;
    }

    analytics = getAnalytics(firebaseApp);
    console.log('Firebase Analytics initialized');

    return analytics;
  } catch (error) {
    console.error('Error initializing Firebase Analytics:', error);
    return null;
  }
};

/**
 * Get Firebase Auth instance
 */
export const getFirebaseAuth = (): Auth | null => {
  try {
    if (!firebaseApp) {
      initializeFirebase();
    }

    if (!firebaseApp) {
      return null;
    }

    if (!auth) {
      auth = getAuth(firebaseApp);
      console.log('Firebase Auth initialized');
    }

    return auth;
  } catch (error) {
    console.error('Error initializing Firebase Auth:', error);
    return null;
  }
};

/**
 * Get Firestore instance
 */
export const getFirebaseFirestore = (): Firestore | null => {
  try {
    if (!firebaseApp) {
      initializeFirebase();
    }

    if (!firebaseApp) {
      return null;
    }

    if (!firestore) {
      firestore = getFirestore(firebaseApp);
      console.log('Firestore initialized');
    }

    return firestore;
  } catch (error) {
    console.error('Error initializing Firestore:', error);
    return null;
  }
};

/**
 * Get Firebase Storage instance
 */
export const getFirebaseStorage = (): FirebaseStorage | null => {
  try {
    if (!firebaseApp) {
      initializeFirebase();
    }

    if (!firebaseApp) {
      return null;
    }

    if (!storage) {
      storage = getStorage(firebaseApp);
      console.log('Firebase Storage initialized');
    }

    return storage;
  } catch (error) {
    console.error('Error initializing Firebase Storage:', error);
    return null;
  }
};

// Export the Firebase app instance
export { firebaseApp };

// Export a helper to check if Firebase is configured
export const isFirebaseConfigured = (): boolean => {
  const config = getFirebaseConfig();
  return Boolean(config.apiKey && config.projectId);
};
